import React, { useState, useEffect } from 'react';
import { Users, Tag, Plus, Edit, Trash2, MessageSquare, Search, X } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface Contact {
  id: string;
  phone_number: string;
  name?: string;
  email?: string;
  tags: string[];
  conversation_id?: string;
  created_at: string;
  last_message_at?: string;
  notes?: string;
  platform: 'whatsapp' | 'facebook' | 'instagram' | 'telegram';
}

interface ContactTag {
  id: string;
  name: string;
  color: string;
  count: number;
}

const ContactManager: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [tags, setTags] = useState<ContactTag[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [showAddContact, setShowAddContact] = useState(false);

  const [editingContact, setEditingContact] = useState<Contact | null>(null);

  // Mock data - replace with API calls
  useEffect(() => {
    const mockContacts: Contact[] = [
      {
        id: '1',
        phone_number: '+1234567890',
        name: 'John Doe',
        email: 'john@example.com',
        tags: ['customer', 'vip'],
        conversation_id: 'conv1',
        created_at: '2024-01-01T00:00:00Z',
        last_message_at: '2024-01-15T10:30:00Z',
        platform: 'whatsapp',
        notes: 'Regular customer, prefers morning contact'
      },
      {
        id: '2',
        phone_number: '+1234567891',
        name: 'Jane Smith',
        tags: ['prospect', 'interested'],
        created_at: '2024-01-02T00:00:00Z',
        last_message_at: '2024-01-14T15:45:00Z',
        platform: 'whatsapp'
      }
    ];

    const mockTags: ContactTag[] = [
      { id: '1', name: 'customer', color: 'blue', count: 1 },
      { id: '2', name: 'vip', color: 'purple', count: 1 },
      { id: '3', name: 'prospect', color: 'green', count: 1 },
      { id: '4', name: 'interested', color: 'yellow', count: 1 }
    ];

    setContacts(mockContacts);
    setTags(mockTags);
  }, []);

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contact.phone_number.includes(searchTerm) ||
                         contact.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTag = selectedTag === '' || contact.tags.includes(selectedTag);
    return matchesSearch && matchesTag;
  });

  const handleSelectContact = (contactId: string) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const handleSelectAll = () => {
    if (selectedContacts.length === filteredContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(filteredContacts.map(c => c.id));
    }
  };



  const handleRemoveTag = (contactId: string, tagName: string) => {
    setContacts(contacts.map(contact => 
      contact.id === contactId 
        ? { ...contact, tags: contact.tags.filter(tag => tag !== tagName) }
        : contact
    ));
    toast.success('Tag removed successfully');
  };

  const handleDeleteContact = (contactId: string) => {
    setContacts(contacts.filter(c => c.id !== contactId));
    setSelectedContacts(selectedContacts.filter(id => id !== contactId));
    toast.success('Contact deleted successfully');
  };

  const handleBulkAction = (action: 'delete' | 'tag') => {
    if (action === 'delete') {
      setContacts(contacts.filter(c => !selectedContacts.includes(c.id)));
      setSelectedContacts([]);
      toast.success(`${selectedContacts.length} contacts deleted`);
    }
  };

  const getTagColor = (tagName: string) => {
    const tag = tags.find(t => t.name === tagName);
    return tag?.color || 'gray';
  };

  const AddContactForm: React.FC<{ contact?: Contact; onSave: (data: Partial<Contact>) => void; onCancel: () => void }> = ({ 
    contact, onSave, onCancel 
  }) => {
    const [formData, setFormData] = useState({
      phone_number: contact?.phone_number || '',
      name: contact?.name || '',
      email: contact?.email || '',
      notes: contact?.notes || '',
      tags: contact?.tags || [],
      platform: contact?.platform || 'whatsapp'
    });

    const [newTag, setNewTag] = useState('');

    const handleAddFormTag = () => {
      if (newTag && !formData.tags.includes(newTag)) {
        setFormData({ ...formData, tags: [...formData.tags, newTag] });
        setNewTag('');
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-96 overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">{contact ? 'Edit Contact' : 'Add Contact'}</h3>
            <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
              <input
                type="tel"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="+1234567890"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Contact name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="email@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
              <select
                value={formData.platform}
                onChange={(e) => setFormData({ ...formData, platform: e.target.value as Contact['platform'] })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="whatsapp">WhatsApp</option>
                <option value="facebook">Facebook</option>
                <option value="instagram">Instagram</option>
                <option value="telegram">Telegram</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
              <div className="flex flex-wrap gap-1 mb-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${getTagColor(tag)}-100 text-${getTagColor(tag)}-800`}
                  >
                    {tag}
                    <button
                      onClick={() => setFormData({ ...formData, tags: formData.tags.filter((_, i) => i !== index) })}
                      className="ml-1 text-xs"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  className="flex-1 border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Add tag"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddFormTag()}
                />
                <button
                  onClick={handleAddFormTag}
                  className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                >
                  Add
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Additional notes about this contact"
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-2 mt-6">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave(formData)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              disabled={!formData.phone_number}
            >
              {contact ? 'Update' : 'Add'} Contact
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 bg-white">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Contact Manager</h2>
          <p className="text-gray-600">Manage your contacts and organize them with tags</p>
        </div>
        <button
          onClick={() => setShowAddContact(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
        >
          <Plus size={16} className="mr-1" />
          Add Contact
        </button>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search contacts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <select
          value={selectedTag}
          onChange={(e) => setSelectedTag(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Tags</option>
          {tags.map(tag => (
            <option key={tag.id} value={tag.name}>
              {tag.name} ({tag.count})
            </option>
          ))}
        </select>
      </div>

      {/* Bulk Actions */}
      {selectedContacts.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
          <div className="flex items-center justify-between">
            <span className="text-blue-800">
              {selectedContacts.length} contact(s) selected
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => handleBulkAction('delete')}
                className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
              >
                Delete Selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tags Overview */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {tags.map(tag => (
            <span
              key={tag.id}
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-${tag.color}-100 text-${tag.color}-800 cursor-pointer hover:bg-${tag.color}-200`}
              onClick={() => setSelectedTag(selectedTag === tag.name ? '' : tag.name)}
            >
              <Tag size={12} className="mr-1" />
              {tag.name} ({tag.count})
            </span>
          ))}
        </div>
      </div>

      {/* Contacts Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={selectedContacts.length === filteredContacts.length && filteredContacts.length > 0}
              onChange={handleSelectAll}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm text-gray-700">
              Select All ({filteredContacts.length})
            </span>
          </div>
        </div>
        
        <ul className="divide-y divide-gray-200">
          {filteredContacts.map((contact) => (
            <li key={contact.id} className="px-6 py-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedContacts.includes(contact.id)}
                  onChange={() => handleSelectContact(contact.id)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                
                <div className="ml-4 flex-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900">
                          {contact.name || 'Unknown'}
                        </p>
                        <span className="ml-2 text-xs text-gray-500">
                          {contact.platform}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">{contact.phone_number}</p>
                      {contact.email && (
                        <p className="text-xs text-gray-400">{contact.email}</p>
                      )}
                      {contact.last_message_at && (
                        <p className="text-xs text-gray-400">
                          Last message: {new Date(contact.last_message_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {/* Tags */}
                      <div className="flex flex-wrap gap-1">
                        {contact.tags.map((tag, index) => (
                          <span
                            key={index}
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${getTagColor(tag)}-100 text-${getTagColor(tag)}-800`}
                          >
                            {tag}
                            <button
                              onClick={() => handleRemoveTag(contact.id, tag)}
                              className="ml-1 text-xs hover:text-red-600"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                      
                      {/* Actions */}
                      <div className="flex space-x-1">
                        {contact.conversation_id && (
                          <button
                            className="text-blue-600 hover:text-blue-700"
                            title="View Conversation"
                          >
                            <MessageSquare size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setEditingContact(contact);
                            setShowAddContact(true);
                          }}
                          className="text-gray-600 hover:text-gray-700"
                          title="Edit Contact"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteContact(contact.id)}
                          className="text-red-600 hover:text-red-700"
                          title="Delete Contact"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {contact.notes && (
                    <div className="mt-2 text-sm text-gray-600 bg-gray-50 p-2 rounded">
                      {contact.notes}
                    </div>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
        
        {filteredContacts.length === 0 && (
          <div className="text-center py-8">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No contacts found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedTag ? 'Try adjusting your search or filter' : 'Get started by adding a new contact'}
            </p>
          </div>
        )}
      </div>

      {/* Add/Edit Contact Modal */}
      {showAddContact && (
        <AddContactForm
          contact={editingContact || undefined}
          onSave={(data) => {
            if (editingContact) {
              setContacts(contacts.map(c => c.id === editingContact.id ? { ...c, ...data } : c));
              toast.success('Contact updated successfully');
            } else {
              const newContact: Contact = {
                id: Date.now().toString(),
                created_at: new Date().toISOString(),
                tags: [],
                platform: 'whatsapp',
                ...data
              } as Contact;
              setContacts([...contacts, newContact]);
              toast.success('Contact added successfully');
            }
            setShowAddContact(false);
            setEditingContact(null);
          }}
          onCancel={() => {
            setShowAddContact(false);
            setEditingContact(null);
          }}
        />
      )}
    </div>
  );
};

export default ContactManager; 